import { useState } from 'react'
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import { ImpersonationBanner } from './ImpersonationBanner.js'
import { OwnerThemeToggle } from './components/OwnerThemeToggle.js'
import { MenuIcon } from './components/icons.js'
import { NAV_ITEMS, type NavItem } from './navItems.js'
import { useOwnerLogout, useOwnerSession } from './hooks/useOwnerSession.js'
import { logoSrc } from './logoSrc.js'

/**
 * Authenticated owner shell — wraps every owner route (dashboard, branding).
 * Mobile-first: a bottom-nav bar with icons + labels under 768px (hamburger
 * sheet for the less-frequent destinations), a left sidebar at ≥768px. The
 * ImpersonationBanner is rendered above the header so it persists across
 * every owner route — that wiring is the 4a-1 fix for 3c's placeholder
 * mounting.
 *
 * Disabled "Coming soon" nav items (Branches/Staff/Customers/Programs/
 * Broadcasts/Billing) render but don't navigate — tapping shows a toast.
 */

function comingSoonToast() {
  toast('Available in the next update')
}

function BottomNav() {
  const [sheetOpen, setSheetOpen] = useState(false)
  const primary = NAV_ITEMS.filter((i) => i.primary)
  const secondary = NAV_ITEMS.filter((i) => !i.primary)

  return (
    <>
      <nav className="owner-bottom-nav" aria-label="Primary">
        {primary.map((item) => (
          <NavItemButton key={item.key} item={item} variant="bottom" />
        ))}
        <button
          type="button"
          className="owner-bottom-nav-item owner-hamburger"
          onClick={() => setSheetOpen(true)}
          aria-label="More"
        >
          <MenuIcon />
          <span>More</span>
        </button>
      </nav>

      {sheetOpen && (
        <>
          <div
            className="owner-sheet-backdrop"
            onClick={() => setSheetOpen(false)}
            aria-hidden="true"
          />
          <div className="owner-sheet" role="dialog" aria-label="More navigation">
            <div className="owner-sheet-handle" />
            {secondary.map((item) => (
              <SheetNavItem
                key={item.key}
                item={item}
                onNavigate={() => setSheetOpen(false)}
              />
            ))}
          </div>
        </>
      )}
    </>
  )
}

function SheetNavItem({
  item,
  onNavigate,
}: {
  item: NavItem
  onNavigate: () => void
}) {
  const Icon = item.icon
  if (!item.to) {
    return (
      <button
        type="button"
        className="owner-sidebar-item is-disabled"
        onClick={comingSoonToast}
      >
        <Icon />
        <span>{item.label}</span>
        <span className="owner-sidebar-soon">Soon</span>
      </button>
    )
  }
  return (
    <NavLink
      to={item.to}
      className={({ isActive }) =>
        `owner-sidebar-item${isActive ? ' is-active' : ''}`
      }
      onClick={onNavigate}
    >
      <Icon />
      <span>{item.label}</span>
    </NavLink>
  )
}

function Sidebar() {
  return (
    <nav className="owner-sidebar" aria-label="Primary">
      {NAV_ITEMS.map((item) => (
        <NavItemButton key={item.key} item={item} variant="sidebar" />
      ))}
    </nav>
  )
}

function NavItemButton({
  item,
  variant,
}: {
  item: NavItem
  variant: 'bottom' | 'sidebar'
}) {
  const Icon = item.icon
  const baseClass =
    variant === 'bottom' ? 'owner-bottom-nav-item' : 'owner-sidebar-item'

  // Disabled "Coming soon" item — renders but doesn't navigate.
  if (!item.to) {
    return (
      <button
        type="button"
        className={`${baseClass} is-disabled`}
        onClick={comingSoonToast}
      >
        <Icon />
        <span>{item.label}</span>
        {variant === 'sidebar' && (
          <span className="owner-sidebar-soon">Soon</span>
        )}
      </button>
    )
  }

  return (
    <NavLink
      to={item.to}
      className={({ isActive }) =>
        `${baseClass}${isActive ? ' is-active' : ''}`
      }
    >
      <Icon />
      <span>{item.label}</span>
    </NavLink>
  )
}

function AccountMenu({
  email,
  onLogout,
}: {
  email: string | null
  onLogout: () => void
}) {
  const [open, setOpen] = useState(false)
  return (
    <div className="owner-menu">
      <button
        type="button"
        className="owner-icon-btn"
        aria-label="Account menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        {/* simple person glyph */}
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <circle cx="12" cy="8" r="3.6" stroke="currentColor" strokeWidth="1.8" />
          <path
            d="M5 20c0-3.6 3-6 7-6s7 2.4 7 6"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
        </svg>
      </button>
      {open && (
        <>
          {/* Dedicated backdrop class — sits BELOW .owner-menu-panel so it
              catches outside-clicks without covering the menu's own
              buttons. (It previously reused .owner-sheet-backdrop, z-40,
              which sat on top of the z-30 panel and ate the Log out tap.) */}
          <div
            className="owner-menu-backdrop"
            onClick={() => setOpen(false)}
            aria-hidden="true"
          />
          <div className="owner-menu-panel" role="menu">
            <div className="owner-menu-email">{email ?? 'Owner'}</div>
            <button
              type="button"
              className="owner-menu-item"
              role="menuitem"
              onClick={() => {
                setOpen(false)
                onLogout()
              }}
            >
              Log out
            </button>
          </div>
        </>
      )}
    </div>
  )
}

export function OwnerShell() {
  const { data: me } = useOwnerSession()
  const logout = useOwnerLogout()
  const navigate = useNavigate()
  const location = useLocation()

  function handleLogout() {
    logout.mutate(undefined, {
      onSettled: () => navigate('/login', { replace: true }),
    })
  }

  const tenant = me?.tenant
  // Header logo: the tenant's custom logo, falling back to the Naya default
  // (same fallback the branding page's LogoUploader uses). Only an empty
  // placeholder while the session is still loading. The custom logo carries a
  // `?v=` cache-bust param (logoSrc) so a re-upload repaints the header
  // without a hard reload — the storage key is overwrite-no-versioning.
  const headerLogoUrl =
    tenant && me
      ? logoSrc(tenant.logo_url, me.default_logo_url, tenant.logo_updated_at)
      : null
  // location is referenced so the shell re-renders on route change for the
  // NavLink active state (NavLink already handles this, but keeping the
  // dependency explicit avoids a lint false-positive on unused import).
  void location

  return (
    <div className="owner-root">
      <div className="owner-shell">
        <ImpersonationBanner />

        <header className="owner-header">
          <Link to="/dashboard" className="owner-header-brand">
            {headerLogoUrl ? (
              <img
                className="owner-header-logo"
                src={headerLogoUrl}
                alt=""
              />
            ) : (
              <span className="owner-header-logo" aria-hidden="true" />
            )}
            <span className="owner-header-name">
              {tenant?.name ?? 'Naya Pass'}
            </span>
          </Link>
          <div className="owner-header-right">
            {tenant && (
              <span
                className={`owner-plan-pill is-${tenant.plan_tier}`}
                aria-label={`Plan: ${tenant.plan_tier}`}
              >
                {tenant.plan_tier}
              </span>
            )}
            <OwnerThemeToggle />
            <AccountMenu
              email={me?.user.email ?? null}
              onLogout={handleLogout}
            />
          </div>
        </header>

        <div className="owner-body">
          <Sidebar />
          <main className="owner-main">
            <Outlet />
          </main>
        </div>

        <BottomNav />
      </div>
    </div>
  )
}
